/**
 * Role Master Page
 *
 * Centralised role database management with tabs for Roles, Import, and Reconciliation.
 * Access restricted to admin and hr_admin roles.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SageCodeMatcher from './SageCodeMatcher';
import SageCodeSyncPanel from './SageCodeSyncPanel';
import OrgStructureTabExtracted from './OrgStructureTab';
import RoleProfilesTab from './RoleProfilesTab';
import { RoleProfileCard } from '@/components/role-profiles/RoleProfileCard';
import BudgetTab from './BudgetTab';
import ReassignPositionsDialog from './ReassignPositionsDialog';
import EditFoJobDialog from './EditFoJobDialog';
import UnifiedRolesTable from './UnifiedRolesTable';
import ManualLinkSearchDialog from './ManualLinkSearchDialog';
import CreateFoJobDialog from './CreateFoJobDialog';
import AutoLinkPreviewDialog from './AutoLinkPreviewDialog';
import FoJobsTab from './FoJobsTab';
import FoPositionsTab from './FoPositionsTab';
import PositionRequestsTab from './PositionRequestsTab';
import {
  BookOpen, Search, Plus, Upload, Download, RefreshCw,
  Edit, Trash2, Eye, FileText, AlertTriangle, CheckCircle, Clock, Settings,
  Filter, X, MoreHorizontal, Loader2, ChevronRight, ArrowRight, Link2,
  Briefcase, Users, DollarSign, ClipboardList,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import { useAuth } from '@/providers/AuthProvider';
import { hasRole, hasAnyPermission, ROLE_MASTER_ROLES, BUDGET_TAB_KEYS, ROLE_PROFILE_KEYS } from '@/auth/permissions';
import roleMasterService, {
  RoleMasterEntry, RoleMasterDetail, RoleMasterStats,
  RoleMasterListParams, FilterOptions, ImportPreviewResponse, ImportResult,
  RoleMasterCreateData, RoleMasterUpdateData, RoleMasterStatus,
  UnifiedRoleMasterEntry, UnifiedStats, UnifiedListParams,
  PushSageCodeResult,
} from '@/services/api/roleMasterService';
import organizationService, { jobFamilySyncService } from '@/services/api/organizationService';

type TabValue = 'roles' | 'jobs' | 'positions' | 'requests' | 'budget' | 'import' | 'profiles' | 'org-structure' | 'sage-sync';

const STATUS_CONFIG: Record<RoleMasterStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  deprecated: { label: 'Deprecated', color: 'bg-gray-100 text-gray-500' },
  draft: { label: 'Draft', color: 'bg-amber-100 text-amber-700' },
};

// Components extracted to separate files:
// - ReconciliationTab.tsx (reconciliation state, auto-link, manual link, create job)
// - OrgStructureTab.tsx (functions, departments, teams, stale jobs with sub-tabs)
// - ReassignJobsDialog.tsx (job reassignment between functions)
// - RoleDetailDrawer.tsx (role detail side drawer)
// - RoleFormDialog.tsx (create/edit form with cascading dropdowns)

// =============================================================================
// DERIVE SAGE CODES BUTTON
// =============================================================================

const DeriveSageCodesButton: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const { confirm: showConfirm } = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<Array<{ id: number; role_code: string; title: string; derived_sage_code: string }> | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const handlePreview = async () => {
    setIsLoading(true);
    setResult(null);
    try {
      const data = await roleMasterService.deriveSageCodes(true);
      setPreview(data.preview);
      if (data.total === 0) {
        setResult('All roles already have Sage codes assigned.');
      }
    } catch (error) {
      console.error('Failed to preview Sage codes:', error);
      setResult('Failed to generate preview.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    if (!preview || preview.length === 0) return;
    const confirmed = await showConfirm(
      `Assign derived Sage codes to ${preview.length} roles?`,
      'Derive Sage Codes',
      { confirmText: `Assign ${preview.length} Codes`, type: 'warning' }
    );
    if (!confirmed) return;
    setIsLoading(true);
    try {
      const data = await roleMasterService.deriveSageCodes(false);
      setResult(`Assigned ${data.applied} Sage codes.`);
      setPreview(null);
      onComplete();
    } catch (error) {
      console.error('Failed to apply Sage codes:', error);
      setResult('Failed to apply codes.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={handlePreview} disabled={isLoading}>
          {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
          Preview
        </Button>
        {preview && preview.length > 0 && (
          <Button size="sm" onClick={handleApply} disabled={isLoading}>
            <CheckCircle className="h-4 w-4 mr-1" /> Apply {preview.length} Codes
          </Button>
        )}
        {result && <span className="text-sm text-gray-500">{result}</span>}
      </div>
      {preview && preview.length > 0 && (
        <div className="border rounded max-h-[300px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500">Code</th>
                <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500">Title</th>
                <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500">Derived Sage Code</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {preview.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-3 py-1 font-mono text-xs text-gray-400">{p.role_code}</td>
                  <td className="px-3 py-1">{p.title}</td>
                  <td className="px-3 py-1 font-mono text-xs font-medium text-emerald-700">{p.derived_sage_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


const PushSageCodesButton: React.FC = () => {
  const { confirm: showConfirm } = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  // Each PushSageCodeResult may target multiple F&O Titles — we flatten for preview.
  const [previewRows, setPreviewRows] = useState<Array<{
    entry_id: number;
    role_code: string;
    target_title_id: string;
    sage_code: string;
  }> | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const flattenPreview = (details: PushSageCodeResult[]) =>
    details.flatMap(d => d.target_title_ids.map(tid => ({
      entry_id: d.entry_id,
      role_code: d.role_code,
      target_title_id: tid,
      sage_code: d.sage_code,
    })));

  const handlePreview = async () => {
    setIsLoading(true);
    setResult(null);
    try {
      const data = await roleMasterService.pushSageCodesToFo(true);
      const rows = flattenPreview(data.details);
      setPreviewRows(rows);
      if (data.total_entries === 0) setResult('No Role Master entries have a Sage code assigned yet.');
    } catch (error: any) {
      console.error('Preview push failed:', error);
      setResult(`Failed: ${error?.response?.data?.detail || 'unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    if (!previewRows || previewRows.length === 0) return;
    const confirmed = await showConfirm(
      `Push ${previewRows.length} Sage codes to F&O Titles (ACCSITitleCode)?`,
      'Push Sage Codes to F&O',
      { confirmText: `Push ${previewRows.length}`, type: 'warning' }
    );
    if (!confirmed) return;
    setIsLoading(true);
    try {
      const data = await roleMasterService.pushSageCodesToFo(false);
      const errorCount = data.errors || 0;
      setResult(`Updated: ${data.updated}, Created: ${data.created}${errorCount > 0 ? `, Errors: ${errorCount}` : ''}`);
      setPreviewRows(null);
    } catch (error: any) {
      console.error('Push failed:', error);
      setResult(`Failed: ${error?.response?.data?.detail || 'unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={handlePreview} disabled={isLoading}>
          {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
          Preview
        </Button>
        {previewRows && previewRows.length > 0 && (
          <Button size="sm" onClick={handleApply} disabled={isLoading}>
            <CheckCircle className="h-4 w-4 mr-1" /> Push {previewRows.length} to F&O
          </Button>
        )}
        {result && <span className="text-sm text-gray-500">{result}</span>}
      </div>
      {previewRows && previewRows.length > 0 && (
        <div className="border rounded max-h-[300px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500">Code</th>
                <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500">F&O Title</th>
                <th className="text-left px-3 py-1.5 text-xs font-medium text-gray-500">Sage Code</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {previewRows.map((p, i) => (
                <tr key={`${p.entry_id}:${p.target_title_id}:${i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-1 font-mono text-xs text-gray-400">{p.role_code}</td>
                  <td className="px-3 py-1 text-xs text-gray-600">{p.target_title_id}</td>
                  <td className="px-3 py-1 font-mono text-xs font-medium text-emerald-700">{p.sage_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

const VALID_TABS: TabValue[] = ['roles', 'jobs', 'positions', 'requests', 'budget', 'import', 'profiles', 'org-structure', 'sage-sync'];

// Search settles before querying. /unified is an expensive build (per-entry F&O
// enrichment), so an un-debounced box turned one typed word into a request per
// character. Matches the 400ms used by ManualLinkSearchDialog.
const SEARCH_DEBOUNCE_MS = 300;

// Remote-work vocabulary, aligned to the HR policy determination
// ("Yes fully; No never; take Limited out, add rotational at partial").
// remote_work is free text in the DB, so anything can be stored — the Select
// below therefore also surfaces whatever the row currently holds, otherwise a
// legacy or out-of-band value renders as an empty dropdown and is silently
// replaced on the next save.
// How the role is allocated across the estate. Replaces the old "Finance
// Campus" field — the finance campus belongs to a position, not to the role.
const ROLE_ALLOCATION_TYPES = [
  { value: 'CE', label: 'CE - National' },
  { value: 'MC', label: 'MC - Regional / Multi-campus' },
  { value: 'C', label: 'C - Campus specific' },
];

const REMOTE_WORK_OPTIONS = [
  'Yes',
  'No',
  'Partially - as per schedule / rotation',
  'Partially - between blocks and rotational',
  'Partially - only between blocks',
];

export const RoleMasterPage: React.FC = () => {
  const { confirm: showConfirm } = useConfirm();
  const { user } = useAuth();
  // Role Master is an admin/hr_admin console (matches the server's
  // ROLE_MASTER_ROLES). NOT the wider ORG_CONSOLE_ROLES — hrbp/fno_admin/
  // data_team have no business in the role database, and the sidebar has always
  // shown this page to admin/hr_admin only. Budget additionally opens to a
  // per-user budget grant, so a Head of Finance can be given that one tab.
  const canSeeAdminTabs = hasRole(user, ROLE_MASTER_ROLES);
  const canSeeBudget = canSeeAdminTabs || hasAnyPermission(user, BUDGET_TAB_KEYS);
  // Role profiles also open to a direct grant: the HR process owner approves profiles through
  // role_profile:approve, and someone capturing profiles can hold role_profile:edit
  // without being an HR admin.
  const canSeeProfiles = canSeeAdminTabs || hasAnyPermission(user, ROLE_PROFILE_KEYS);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') as TabValue | null;
  const requestedTab: TabValue = urlTab && VALID_TABS.includes(urlTab) ? urlTab : 'roles';
  // Coerce to a tab this user may actually open — otherwise ?tab=roles would
  // render the roles panel (and fire its requests) for a budget-only grantee.
  const activeTab: TabValue =
    canSeeAdminTabs ? requestedTab
      : (requestedTab === 'profiles' && canSeeProfiles) ? 'profiles'
      : (canSeeBudget ? 'budget' : (canSeeProfiles ? 'profiles' : requestedTab));
  const setActiveTab = (value: TabValue) => {
    setSearchParams({ tab: value });
  };
  const [isLoading, setIsLoading] = useState(true);
  const [entries, setEntries] = useState<UnifiedRoleMasterEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [unifiedStats, setUnifiedStats] = useState<UnifiedStats | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [reassignSource, setReassignSource] = useState<UnifiedRoleMasterEntry | null>(null);
  const [editFoJobId, setEditFoJobId] = useState<string | null>(null);
  const [editFoJobDefaults, setEditFoJobDefaults] = useState<{ functional_area?: string; description?: string; grade_level?: string }>({});
  // F&O dialog state
  const [manualLinkEntry, setManualLinkEntry] = useState<UnifiedRoleMasterEntry | null>(null);
  const [createJobEntry, setCreateJobEntry] = useState<UnifiedRoleMasterEntry | null>(null);
  const [autoLinkOpen, setAutoLinkOpen] = useState(false);
  const [linkStatusFilter, setLinkStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // The value the list actually queries on. searchQuery stays the controlled
  // input value (so typing is never laggy); this trails it by DEBOUNCE_MS so a
  // word typed at speed costs one request instead of one per character.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [functionalAreaFilter, setFunctionalAreaFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [team1Filter, setTeam1Filter] = useState<string>('all');
  const [team2Filter, setTeam2Filter] = useState<string>('all');
  const [team3Filter, setTeam3Filter] = useState<string>('all');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [rolesFiltersExpanded, setRolesFiltersExpanded] = useState(false);

  const rolesActiveFilterCount = useMemo(() => {
    let count = 0;
    if (functionalAreaFilter !== 'all') count++;
    if (departmentFilter !== 'all') count++;
    if (team1Filter !== 'all') count++;
    if (team2Filter !== 'all') count++;
    if (team3Filter !== 'all') count++;
    if (gradeFilter !== 'all') count++;
    return count;
  }, [functionalAreaFilter, departmentFilter, team1Filter, team2Filter, team3Filter, gradeFilter]);

  // Detail drawer
  const [selectedEntry, setSelectedEntry] = useState<RoleMasterDetail | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Create/Edit dialog
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<RoleMasterEntry | null>(null);
  const [formData, setFormData] = useState<RoleMasterCreateData>({
    title: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Create-only: auto-create + link the F&O (Dynamics) job (JobId = the new role_code).
  const [createFoJob, setCreateFoJob] = useState(true);

  // Org unit data for cascading dropdowns
  // Cascading dropdown options derived from the live Role Master tree. Teams link to
  // their parent by NAME (the taxonomy is a projection of Role Master, not OrgUnit ids).
  const [orgFunctions, setOrgFunctions] = useState<Array<{ name: string }>>([]);
  const [orgDepartments, setOrgDepartments] = useState<Array<{ name: string }>>([]);
  const [orgTeams1, setOrgTeams1] = useState<Array<{ name: string; parent_name: string }>>([]);
  const [orgTeams2, setOrgTeams2] = useState<Array<{ name: string; parent_name: string }>>([]);
  const [orgTeams3, setOrgTeams3] = useState<Array<{ name: string; parent_name: string }>>([]);
  const [addingNewTeam, setAddingNewTeam] = useState<{ level: 1 | 2 | 3 } | null>(null);
  const [newTeamName, setNewTeamName] = useState('');

  // Load org unit reference data for dropdowns
  const loadOrgUnits = useCallback(async () => {
    try {
      // Source the cascade from the live Role Master projection (functional_area /
      // department / team_1-3), so options always reflect current role data.
      const tree = await organizationService.getOrgUnitTree();
      setOrgFunctions((tree.functions || []).map(f => ({ name: f.name })));
      setOrgDepartments((tree.departments || []).map(d => ({ name: d.name })));
      const t1: Array<{ name: string; parent_name: string }> = [];
      const t2: Array<{ name: string; parent_name: string }> = [];
      const t3: Array<{ name: string; parent_name: string }> = [];
      for (const d of tree.departments || []) {
        for (const a of d.children || []) {
          t1.push({ name: a.name, parent_name: d.name });
          for (const b of a.children || []) {
            t2.push({ name: b.name, parent_name: a.name });
            for (const c of b.children || []) {
              t3.push({ name: c.name, parent_name: b.name });
            }
          }
        }
      }
      const dedup = (arr: Array<{ name: string; parent_name: string }>) => {
        const seen = new Set<string>();
        return arr.filter(x => {
          const k = `${x.parent_name}\u0000${x.name}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        }).sort((a, b) => a.name.localeCompare(b.name));
      };
      setOrgTeams1(dedup(t1));
      setOrgTeams2(dedup(t2));
      setOrgTeams3(dedup(t3));
    } catch (error) {
      console.error('Failed to load org units for dropdowns:', error);
    }
  }, []);

  // Teams are a projection of Role Master — "adding" one just sets the value on the
  // role; it materialises in the tree once the role is saved (no OrgUnit row created).
  const handleCreateTeam = (level: 1 | 2 | 3, name: string) => {
    const v = name.trim();
    if (!v) return;
    if (level === 1) setFormData(prev => ({ ...prev, team_1: v, team_2: undefined, team_3: undefined }));
    if (level === 2) setFormData(prev => ({ ...prev, team_2: v, team_3: undefined }));
    if (level === 3) setFormData(prev => ({ ...prev, team_3: v }));
    setAddingNewTeam(null);
    setNewTeamName('');
  };

  // Import state
  const [importPreview, setImportPreview] = useState<ImportPreviewResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Import-only: also create + link an F&O (Dynamics) job for each new role.
  const [importCreateFoJobs, setImportCreateFoJobs] = useState(false);

  // Trail the controlled input value so a word typed at speed costs one
  // request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // The dropdown options don't vary with page/search/filters, so they load once
  // rather than riding along with every list request. Re-run them explicitly
  // after mutations that can mint a new department/team value.
  const loadFilterOptions = useCallback(async () => {
    if (!canSeeAdminTabs) return;
    try {
      setFilterOptions(await roleMasterService.getFilterOptions());
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  }, [canSeeAdminTabs]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Monotonic request id. Responses can land out of order (a filter click or
  // Refresh overlaps the in-flight load), and the loser must not overwrite the
  // winner's rows or clear its spinner.
  const listReqRef = useRef(0);

  const loadData = useCallback(async () => {
    // This is the Roles tab's data. It loads from a page-level effect rather
    // than the panel, so without this guard a budget-only grantee (who can't
    // see the Roles tab at all) would still fire — and 403 on — the request;
    // and landing on ?tab=budget would pay for a full unified build first.
    if (!canSeeAdminTabs || activeTab !== 'roles') {
      setIsLoading(false);
      return;
    }
    const reqId = ++listReqRef.current;
    setIsLoading(true);
    try {
      const params: UnifiedListParams = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter as RoleMasterStatus;
      if (linkStatusFilter !== 'all') params.link_status = linkStatusFilter as any;
      if (functionalAreaFilter !== 'all') params.functional_area = functionalAreaFilter;
      if (departmentFilter !== 'all') params.department = departmentFilter;
      if (team1Filter !== 'all') params.team_1 = team1Filter;
      if (team2Filter !== 'all') params.team_2 = team2Filter;
      if (team3Filter !== 'all') params.team_3 = team3Filter;
      if (gradeFilter !== 'all') params.grade_level = gradeFilter;

      const listData = await roleMasterService.listUnified(params);

      if (reqId !== listReqRef.current) return;  // superseded — discard
      setEntries(listData.items);
      setTotalEntries(listData.total);
      setUnifiedStats(listData.stats);
    } catch (error) {
      if (reqId !== listReqRef.current) return;
      console.error('Failed to load role master data:', error);
    } finally {
      // Only the newest request owns the spinner.
      if (reqId === listReqRef.current) setIsLoading(false);
    }
  }, [canSeeAdminTabs, activeTab, page, pageSize, debouncedSearch, statusFilter, linkStatusFilter, functionalAreaFilter, departmentFilter, team1Filter, team2Filter, team3Filter, gradeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  // ── Grade-sync bulk push ─────────────────────────────────────────────
  // Pushes RM.grade_level → F&O JobCompensations for EVERY issue across the
  // whole RM (not just the current page). The unified-list endpoint reports
  // global counts in stats.grade_mismatch_count + grade_missing_count, and
  // /grade-sync-issues returns the full set of entries to push.
  const [isPushingGrades, setIsPushingGrades] = useState(false);
  const [pushGradesMessage, setPushGradesMessage] = useState<string | null>(null);

  const handlePushAllGrades = async () => {
    setIsPushingGrades(true);
    setPushGradesMessage(null);
    try {
      // Step 1: fetch every entry with a grade-sync issue
      const issuesData = await roleMasterService.getGradeSyncIssues();
      if (issuesData.total === 0) {
        setPushGradesMessage('No grade-sync issues found.');
        return;
      }

      // Step 2: confirm with the user — break it down so they know exactly
      // what will happen (REPLACE vs CREATE).
      const confirmed = await showConfirm(
        `Push ${issuesData.total} RM grades to F&O? ` +
        (issuesData.mismatch_count > 0
          ? `${issuesData.mismatch_count} will REPLACE the existing F&O grade. `
          : '') +
        (issuesData.missing_count > 0
          ? `${issuesData.missing_count} will be CREATED in F&O.`
          : ''),
        'Push grades to F&O',
        { confirmText: `Push ${issuesData.total} to F&O`, type: 'warning' },
      );
      if (!confirmed) return;

      // Step 3: post the bulk update
      const result = await roleMasterService.bulkUpdateCompensations(
        issuesData.issues.map((i) => ({
          job_id: i.fo_job_id,
          compensation_level: i.grade_level,
          existing_level: i.fo_compensation_level ?? null,
        })),
      );

      setPushGradesMessage(
        `Pushed ${result.updated + result.created} grades to F&O ` +
        `(${result.updated} replaced, ${result.created} created` +
        (result.skipped > 0 ? `, ${result.skipped} already in sync` : '') +
        (result.errors > 0 ? `, ${result.errors} errors` : '') +
        ').',
      );

      // Reload the table so the rows now reflect the new F&O state
      loadData();
    } catch (e: any) {
      console.error('Push grades failed:', e);
      setPushGradesMessage(
        `Push failed: ${e?.response?.data?.detail || e?.message || 'unknown error'}`,
      );
    } finally {
      setIsPushingGrades(false);
    }
  };

  const handleOpenDetail = async (entry: RoleMasterEntry) => {
    try {
      const detail = await roleMasterService.get(entry.id);
      setSelectedEntry(detail);
      setIsDetailOpen(true);
    } catch (error) {
      console.error('Failed to load entry details:', error);
    }
  };

  const handleOpenCreate = () => {
    setEditingEntry(null);
    setFormData({ title: '' });
    setCreateFoJob(true);
    setFormError(null);
    loadOrgUnits();
    setIsFormOpen(true);
  };

  const handleOpenEdit = (entry: RoleMasterEntry) => {
    setIsDetailOpen(false);
    setEditingEntry(entry);
    setFormData({
      title: entry.title,
      job_family: entry.job_family || undefined,
      functional_area: entry.functional_area || undefined,
      grade_level: entry.grade_level || undefined,
      department: entry.department || undefined,
      team_1: entry.team_1 || undefined,
      team_2: entry.team_2 || undefined,
      team_3: entry.team_3 || undefined,
      is_academic: entry.is_academic,
      position_level: entry.position_level || undefined,
      sage_title_code: entry.sage_title_code || undefined,
      remco_grade: entry.remco_grade || undefined,
      remchannel_match_code: entry.remchannel_match_code || undefined,
      remchannel_grade_match: entry.remchannel_grade_match || undefined,
      remchannel_title_match: entry.remchannel_title_match || undefined,
      finance_department_name: entry.finance_department_name || undefined,
      finance_department_code: entry.finance_department_code || undefined,
      role_allocation_type: entry.role_allocation_type || undefined,
      essential_campus_operations: entry.essential_campus_operations ?? undefined,
      campus_manco_member: entry.campus_manco_member ?? undefined,
      remote_work: entry.remote_work || undefined,
      customer_facing: entry.customer_facing ?? undefined,
      revenue_generating: entry.revenue_generating || undefined,
      is_critical_role: entry.is_critical_role ?? undefined,
      is_critical_cohort: entry.is_critical_cohort ?? undefined,
      occupational_level: entry.occupational_level || undefined,
      budget_status: entry.budget_status || undefined,
      role_cluster_skills: entry.role_cluster_skills || undefined,
      role_cluster_grade_1: entry.role_cluster_grade_1 || undefined,
      role_cluster_grade_2: entry.role_cluster_grade_2 || undefined,
      notes: entry.notes || undefined,
      effective_date: entry.effective_date || undefined,
      expiration_date: entry.expiration_date || undefined,
    });
    setFormError(null);
    loadOrgUnits();
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setFormError(null);
    try {
      if (editingEntry) {
        const updateData: RoleMasterUpdateData = {
          title: formData.title,
          job_family: formData.job_family,
          functional_area: formData.functional_area,
          grade_level: formData.grade_level,
          department: formData.department,
          team_1: formData.team_1,
          team_2: formData.team_2,
          team_3: formData.team_3,
          is_academic: formData.is_academic,
          position_level: formData.position_level,
          sage_title_code: formData.sage_title_code,
          remco_grade: formData.remco_grade,
          remchannel_match_code: formData.remchannel_match_code,
          remchannel_grade_match: formData.remchannel_grade_match,
          remchannel_title_match: formData.remchannel_title_match,
          finance_department_name: formData.finance_department_name,
          finance_department_code: formData.finance_department_code,
          role_allocation_type: formData.role_allocation_type,
          essential_campus_operations: formData.essential_campus_operations,
          campus_manco_member: formData.campus_manco_member,
          remote_work: formData.remote_work,
          customer_facing: formData.customer_facing,
          revenue_generating: formData.revenue_generating,
          is_critical_role: formData.is_critical_role,
          is_critical_cohort: formData.is_critical_cohort,
          occupational_level: formData.occupational_level,
          budget_status: formData.budget_status,
          role_cluster_skills: formData.role_cluster_skills,
          role_cluster_grade_1: formData.role_cluster_grade_1,
          role_cluster_grade_2: formData.role_cluster_grade_2,
          notes: formData.notes,
          effective_date: formData.effective_date,
          expiration_date: formData.expiration_date,
        };

        // PUT is a PARTIAL update — the API only touches keys present in the
        // body — and JSON.stringify omits `undefined` entirely. So clearing a
        // field (picking "— None —") sent nothing at all and the old value
        // survived the save: it looked saved, then reappeared on the next
        // export. Send an explicit null so the field is actually cleared.
        // Safe because the Edit form initialises every key it sends, so only a
        // genuinely-cleared field is undefined here.
        const payload = updateData as Record<string, unknown>;
        for (const key of Object.keys(payload)) {
          if (payload[key] === undefined) payload[key] = null;
        }
        await roleMasterService.update(editingEntry.id, updateData);
        setIsFormOpen(false);
        loadData();
        loadFilterOptions();  // an edit can introduce a new department/team value
      } else {
        const entry = await roleMasterService.create(formData);

        // Optionally auto-create + link the F&O (Dynamics) job. Non-blocking:
        // the role IS created regardless of what happens with F&O.
        let foWarning: string | null = null;
        if (createFoJob) {
          const cleanTitle = (formData.title || '').replace(/\s*\([^)]*\)\s*/g, ' ').trim();
          try {
            const res = await jobFamilySyncService.createJob({
              job_id: entry.role_code,
              title_id: cleanTitle,
              description: cleanTitle,
              job_family_id: formData.functional_area || undefined,
              // Prefer the created entry's code — the backend auto-derives one
              // when the form left it blank (F&O Titles requires ACCSITitleCode).
              sage_title_code: entry.sage_title_code || formData.sage_title_code || undefined,
              compensation_level: formData.grade_level || undefined,
            });
            if (res?.job_created) {
              await roleMasterService.linkJob(entry.id, entry.role_code, cleanTitle, false);
            }
            const problems = [
              ...((res?.errors || []).map((er: any) =>
                er?.error ? `${er.entity}${er.key ? ` (${er.key})` : ''}: ${er.error}` : String(er))),
              ...((res?.warnings as string[] | undefined) || []),
            ];
            if (!res?.job_created) {
              foWarning = `Role ${entry.role_code} created, but its F&O job was not created` +
                (problems.length ? `: ${problems.join('; ')}` : '.') +
                ' You can create it later from the role’s menu.';
            } else if (problems.length) {
              foWarning = `Role ${entry.role_code} created and F&O job linked, with warnings: ${problems.join('; ')}`;
            }
          } catch (e: any) {
            console.error('F&O job create/link failed:', e);
            foWarning = `Role ${entry.role_code} created, but the F&O job failed: ` +
              `${e?.response?.data?.detail || e?.message || 'unknown error'}.` +
              ' You can create it later from the role’s menu.';
          }
        }

        loadData();
        loadFilterOptions();  // a new role can introduce a new department/team value
        if (foWarning) {
          // Keep the panel open (now in edit mode for the created role) so the F&O
          // outcome is visible and the user can't accidentally create a duplicate.
          setEditingEntry(entry);
          setFormError(foWarning);
        } else {
          setIsFormOpen(false);
        }
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setFormError(detail || 'Failed to save entry');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeprecate = async (entry: RoleMasterEntry) => {
    const confirmed = await showConfirm(
      `Deprecate role "${entry.title}" (${entry.role_code})?`,
      'Deprecate Role',
      { confirmText: 'Deprecate', type: 'danger' }
    );
    if (!confirmed) return;
    try {
      await roleMasterService.deprecate(entry.id, 'Deprecated via UI');
      loadData();
    } catch (error) {
      console.error('Failed to deprecate entry:', error);
    }
  };

  const handleReactivate = async (entry: RoleMasterEntry) => {
    const confirmed = await showConfirm(
      `Reactivate role "${entry.title}" (${entry.role_code})? It will return to Active status.`,
      'Reactivate Role',
      { confirmText: 'Reactivate', type: 'info' }
    );
    if (!confirmed) return;
    try {
      await roleMasterService.reactivate(entry.id, 'Reactivated via UI');
      loadData();
    } catch (error) {
      console.error('Failed to reactivate entry:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsImporting(true);
    setImportResult(null);
    setImportCreateFoJobs(false);
    try {
      const preview = await roleMasterService.previewImport(file);
      setImportPreview(preview);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      alert(detail || 'Failed to parse file');
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const handleConfirmImport = async (skipDuplicates: boolean, updateExisting: boolean) => {
    if (!importPreview) return;
    setIsImporting(true);
    try {
      const result = await roleMasterService.confirmImport(
        importPreview,
        skipDuplicates,
        updateExisting,
        importCreateFoJobs,
      );
      setImportResult(result);
      setImportPreview(null);
      loadData();
      loadFilterOptions();  // an import can introduce new department/team values
    } catch (error) {
      console.error('Failed to confirm import:', error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await roleMasterService.exportExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `role_master_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(totalEntries / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-heritage-blue" />
            Role Master
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Centralised role database and position classification
          </p>
        </div>
        {/* Export / Refresh / Add Role live in each tab's own toolbar (below) so the
            button position stays consistent across tabs — see the Roles filter row. */}
      </div>

      {/* Stats Cards */}
      {unifiedStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-heritage-blue">{unifiedStats.total_entries}</div>
              <div className="text-xs text-gray-500">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-emerald-600">{unifiedStats.active_entries}</div>
              <div className="text-xs text-gray-500">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-amber-600">{unifiedStats.draft_entries}</div>
              <div className="text-xs text-gray-500">Draft</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-gray-400">{unifiedStats.deprecated_entries}</div>
              <div className="text-xs text-gray-500">Deprecated</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-emerald-700">{unifiedStats.linked_count}</div>
              <div className="text-xs text-gray-500">Linked</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-orange-500">{unifiedStats.unlinked_count}</div>
              <div className="text-xs text-gray-500">Unlinked</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-amber-500">{unifiedStats.mismatch_count}</div>
              <div className="text-xs text-gray-500">Mismatches</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grade-sync banner — only renders when there are issues. Shows
          global counts (across the whole RM, not the current page) and
          a single button that pushes EVERY out-of-sync RM grade into
          F&O JobCompensations via the bulk endpoint. */}
      {unifiedStats && (
        ((unifiedStats.grade_mismatch_count ?? 0) > 0 ||
         (unifiedStats.grade_missing_count ?? 0) > 0)
      ) && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="py-3 px-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium text-sm">Grade sync issues</span>
            </div>
            <div className="text-xs text-gray-700 flex items-center gap-3">
              <span>
                <span className="text-red-600 font-semibold">
                  {unifiedStats.grade_mismatch_count ?? 0}
                </span>{' '}
                <span className="text-gray-500">different in F&amp;O</span>
              </span>
              <span className="text-gray-300">·</span>
              <span>
                <span className="text-amber-600 font-semibold">
                  {unifiedStats.grade_missing_count ?? 0}
                </span>{' '}
                <span className="text-gray-500">missing in F&amp;O</span>
              </span>
              {(unifiedStats.grade_only_in_fo_count ?? 0) > 0 && (
                <>
                  <span className="text-gray-300">·</span>
                  <span>
                    <span className="text-amber-700 font-semibold">
                      {unifiedStats.grade_only_in_fo_count ?? 0}
                    </span>{' '}
                    <span className="text-gray-500">in F&amp;O only</span>
                  </span>
                </>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
              disabled={isPushingGrades}
              onClick={handlePushAllGrades}
              title="Push every out-of-sync RM grade to F&O JobCompensations"
            >
              {isPushingGrades ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Pushing...
                </>
              ) : (
                <>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Push{' '}
                  {(unifiedStats.grade_mismatch_count ?? 0) +
                    (unifiedStats.grade_missing_count ?? 0)}{' '}
                  grades to F&amp;O
                </>
              )}
            </Button>
            {pushGradesMessage && (
              <div className="w-full text-xs text-gray-700 pt-1">{pushGradesMessage}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
        <TabsList>
          {canSeeAdminTabs && (
          <TabsTrigger value="roles">
            <FileText className="h-4 w-4 mr-1" />
            Roles
          </TabsTrigger>
          )}
          {canSeeAdminTabs && (
          <TabsTrigger value="jobs">
            <Briefcase className="h-4 w-4 mr-1" />
            Jobs
          </TabsTrigger>
          )}
          {canSeeAdminTabs && (
          <TabsTrigger value="positions">
            <Users className="h-4 w-4 mr-1" />
            Positions
          </TabsTrigger>
          )}
          {canSeeAdminTabs && (
          <TabsTrigger value="requests">
            <ClipboardList className="h-4 w-4 mr-1" />
            Requests
          </TabsTrigger>
          )}
          {canSeeBudget && (
          <TabsTrigger value="budget">
            <DollarSign className="h-4 w-4 mr-1" />
            Budget
          </TabsTrigger>
          )}
          {canSeeAdminTabs && (
          <TabsTrigger value="import">
            <Upload className="h-4 w-4 mr-1" />
            Import
          </TabsTrigger>
          )}
          {canSeeProfiles && (
          <TabsTrigger value="profiles">
            <FileText className="h-4 w-4 mr-1" />
            Profiles
          </TabsTrigger>
          )}
          {canSeeAdminTabs && (
          <TabsTrigger value="org-structure">
            <Settings className="h-4 w-4 mr-1" />
            Org Structure
          </TabsTrigger>
          )}
          {canSeeAdminTabs && (
          <TabsTrigger value="sage-sync">
            <RefreshCw className="h-4 w-4 mr-1" />
            Sage Sync
          </TabsTrigger>
          )}
        </TabsList>

        {/* Roles Tab (Unified: Roles + F&O Reconciliation) */}
        <TabsContent value="roles" className="space-y-4">
          {/* Filters */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by title, code, or job ID..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="deprecated">Deprecated</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
              <Select value={linkStatusFilter} onValueChange={(v) => { setLinkStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="F&O Link" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Links</SelectItem>
                  <SelectItem value="linked">Linked</SelectItem>
                  <SelectItem value="unlinked">Unlinked</SelectItem>
                  <SelectItem value="mismatch">Mismatches</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs">
                    <MoreHorizontal className="h-3.5 w-3.5 mr-1" /> Bulk Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setAutoLinkOpen(true)}>
                    <Link2 className="h-4 w-4 mr-2" /> Auto-Link to F&O
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => {
                    const confirmed = await showConfirm('Derive positions from F&O job linkages?', 'Derive Positions', { confirmText: 'Derive', type: 'warning' });
                    if (!confirmed) return;
                    try { await roleMasterService.derivePositions(); loadData(); } catch (e) { console.error(e); }
                  }}>
                    <ArrowRight className="h-4 w-4 mr-2" /> Derive Positions
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline" size="sm"
                onClick={() => setRolesFiltersExpanded(!rolesFiltersExpanded)}
                className="text-xs"
              >
                <Filter className="h-3.5 w-3.5 mr-1" />
                {rolesFiltersExpanded ? 'Less Filters' : 'More Filters'}
                {rolesActiveFilterCount > 0 && (
                  <Badge className="ml-1.5 bg-heritage-blue text-white text-[10px] px-1.5 py-0">{rolesActiveFilterCount}</Badge>
                )}
              </Button>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting}>
                <Download className="h-4 w-4 mr-1" /> Export
              </Button>
              <Button variant="outline" size="sm" onClick={loadData}>
                <RefreshCw className={cn("h-4 w-4 mr-1", isLoading && "animate-spin")} /> Refresh
              </Button>
              <Button size="sm" onClick={handleOpenCreate}>
                <Plus className="h-4 w-4 mr-1" /> Add Role
              </Button>
            </div>
            {rolesFiltersExpanded && filterOptions && (
              <div className="flex flex-wrap gap-3 items-center p-3 bg-gray-50 rounded-lg border">
                <Select value={functionalAreaFilter} onValueChange={(v) => { setFunctionalAreaFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Function" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Functions</SelectItem>
                    {filterOptions.functional_areas.filter(Boolean).map((fa) => (
                      <SelectItem key={fa} value={fa}>{fa}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={departmentFilter} onValueChange={(v) => { setDepartmentFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {filterOptions.departments?.filter(Boolean).map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={team1Filter} onValueChange={(v) => { setTeam1Filter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Team 1" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team 1</SelectItem>
                    {filterOptions.teams_1?.filter(Boolean).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={team2Filter} onValueChange={(v) => { setTeam2Filter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Team 2" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team 2</SelectItem>
                    {filterOptions.teams_2?.filter(Boolean).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={team3Filter} onValueChange={(v) => { setTeam3Filter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Team 3" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team 3</SelectItem>
                    {filterOptions.teams_3?.filter(Boolean).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={gradeFilter} onValueChange={(v) => { setGradeFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {filterOptions.grade_levels.filter(Boolean).map((gl) => (
                      <SelectItem key={gl} value={gl}>{gl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rolesActiveFilterCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => { setFunctionalAreaFilter('all'); setDepartmentFilter('all'); setTeam1Filter('all'); setTeam2Filter('all'); setTeam3Filter('all'); setGradeFilter('all'); setPage(1); }}>
                    <X className="h-3 w-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Unified Table.
              Only the very first load (nothing to show yet) gets the block
              spinner. Subsequent refetches keep the current rows mounted and
              just dim them — swapping the table out on every keystroke, filter
              click and page turn was collapsing the page height and throwing
              away the user's scroll position. */}
          {isLoading && entries.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading roles...
            </div>
          ) : (
            <div className={cn('transition-opacity duration-150', isLoading && 'opacity-60 pointer-events-none')}>
            <UnifiedRolesTable
              entries={entries}
              onRowClick={(entry) => handleOpenDetail(entry)}
              onOpenSageSync={(entry) => {
                setSearchParams({ tab: 'sage-sync', sync_focus: String(entry.id) });
              }}
              actionHandlers={{
                onViewDetails: (entry) => handleOpenDetail(entry),
                onEdit: (entry) => handleOpenEdit(entry),
                onReassignPositions: (entry) => setReassignSource(entry),
                onDeprecate: (entry) => handleDeprecate(entry),
                onReactivate: (entry) => handleReactivate(entry),
                onLinkSuggestion: async (entry) => {
                  const s = entry.suggestions?.[0];
                  if (!s) return;
                  const ok = await showConfirm(
                    `Link "${entry.title}" to F&O Job "${s.job_id}" (${Math.round(s.confidence * 100)}% match)?`,
                    'Link to F&O Job',
                    { confirmText: 'Link', type: 'warning' },
                  );
                  if (!ok) return;
                  try { await roleMasterService.linkJob(entry.id, s.job_id, s.fo_title_id); loadData(); } catch (e) { console.error(e); }
                },
                onSearchFoJobs: (entry) => setManualLinkEntry(entry),
                onCreateFoJob: (entry) => setCreateJobEntry(entry),
                onEditFoJob: (entry) => { setEditFoJobId(entry.fo_job_id || null); setEditFoJobDefaults({ functional_area: entry.functional_area, grade_level: entry.grade_level }); },
                onUpdateFoTitle: async (entry) => {
                  if (!entry.fo_job_id) return;
                  const ok = await showConfirm(
                    `Update F&O Job title to "${entry.title}"? This will cascade to linked positions and workers.`,
                    'Update F&O Title',
                    { confirmText: 'Update', type: 'warning' },
                  );
                  if (!ok) return;
                  try { await roleMasterService.updateFoTitle(entry.id); loadData(); } catch (e) { console.error(e); }
                },
                onAcceptVariant: async (entry) => {
                  const s = entry.suggestions?.[0];
                  if (!entry.fo_job_id) return;
                  // fo_title_id must only hold values known to exist in F&O — never seed it
                  // from the raw RM title (parentheticals/suffixes would poison the mirror).
                  try { await roleMasterService.linkJob(entry.id, entry.fo_job_id!, entry.fo_title_id || '', false); loadData(); } catch (e) { console.error(e); }
                },
                onAdoptFoGrade: async (entry) => {
                  const ok = await showConfirm(
                    `Adopt F&O grade ${entry.fo_compensation_level} into Role Master for ${entry.role_code}?`,
                    'Adopt F&O grade',
                    { confirmText: 'Adopt', type: 'warning' },
                  );
                  if (!ok) return;
                  try { await roleMasterService.adoptFoGrade([entry.id]); loadData(); } catch (e) { console.error(e); }
                },
                onUnlink: async (entry) => {
                  const ok = await showConfirm(
                    `Unlink "${entry.title}" from F&O Job "${entry.fo_job_id}"?`,
                    'Unlink from F&O',
                    { confirmText: 'Unlink', type: 'danger' },
                  );
                  if (!ok) return;
                  try { await roleMasterService.unlinkJob(entry.id); loadData(); } catch (e) { console.error(e); }
                },
              }}
            />
            </div>
          )}

          {/* Pagination */}
          {totalEntries > pageSize && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-500">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalEntries)} of {totalEntries}
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(totalEntries / pageSize)} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Jobs Tab (F&O Jobs management) — forceMount keeps it in DOM so
            fetched F&O data isn't re-fetched on every tab switch */}
        {canSeeAdminTabs && (
        <TabsContent value="jobs" forceMount className="space-y-4 data-[state=inactive]:hidden">
          <FoJobsTab onNavigateToRole={(roleId) => {
            // Navigate to Roles tab filtered by this role
            setActiveTab('roles');
            setSearchQuery(String(roleId));
            setPage(1);
          }} />
        </TabsContent>
        )}

        {/* Positions Tab (F&O Positions management) — forceMount, see above */}
        {canSeeAdminTabs && (
        <TabsContent value="positions" forceMount className="space-y-4 data-[state=inactive]:hidden">
          <FoPositionsTab />
        </TabsContent>
        )}

        {/* Position Requests Tab — review & provision staged "create positions" requests */}
        <TabsContent value="requests" className="space-y-4">
          <PositionRequestsTab />
        </TabsContent>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Import from Excel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload an Excel (.xlsx) or CSV file with a <code className="bg-gray-100 px-1 rounded">title</code> column.
                Role codes are auto-generated (RM-0001, RM-0002, ...) if no <code className="bg-gray-100 px-1 rounded">role_code</code> column is present.
                Optional columns: function, grade, department, team_1, team_2, team_3.
              </p>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={isImporting}
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-heritage-blue text-white rounded-md hover:bg-heritage-blue/90 transition-colors text-sm font-medium">
                    <Upload className="h-4 w-4" />
                    {isImporting ? 'Processing...' : 'Upload File'}
                  </div>
                </label>
              </div>

              {/* Preview */}
              {importPreview && (
                <div className="space-y-4 border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Preview: {importPreview.filename}</h3>
                    <Button variant="ghost" size="sm" onClick={() => setImportPreview(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><span className="text-gray-500">Total rows:</span> <strong>{importPreview.total_rows}</strong></div>
                    <div><span className="text-emerald-600">New:</span> <strong>{importPreview.new_entries}</strong></div>
                    <div><span className="text-blue-600">Existing (will update):</span> <strong>{importPreview.updates}</strong></div>
                  </div>
                  {importPreview.error_details.length > 0 && (
                    <div className="bg-red-50 p-3 rounded text-sm text-red-700">
                      <p className="font-medium">Errors ({importPreview.errors}):</p>
                      <ul className="list-disc list-inside mt-1">
                        {importPreview.error_details.slice(0, 10).map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Preview table */}
                  <div className="max-h-[300px] overflow-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Row</TableHead>
                          <TableHead className="whitespace-nowrap">Status</TableHead>
                          <TableHead className="whitespace-nowrap">Code</TableHead>
                          <TableHead className="whitespace-nowrap">Title</TableHead>
                          <TableHead className="whitespace-nowrap">Function</TableHead>
                          <TableHead className="whitespace-nowrap">Department</TableHead>
                          <TableHead className="whitespace-nowrap">Team 1</TableHead>
                          <TableHead className="whitespace-nowrap">Team 2</TableHead>
                          <TableHead className="whitespace-nowrap">Team 3</TableHead>
                          <TableHead className="whitespace-nowrap">Grade</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.preview_rows.map((row) => (
                          <TableRow key={row.row_number}>
                            <TableCell>{row.row_number}</TableCell>
                            <TableCell>
                              <Badge className={cn('text-xs',
                                row.status === 'new' ? 'bg-emerald-100 text-emerald-700' :
                                row.status === 'duplicate' ? 'bg-gray-100 text-gray-500' :
                                'bg-blue-100 text-blue-700'
                              )}>
                                {row.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{row.role_code}</TableCell>
                            <TableCell className="whitespace-nowrap">{row.title}</TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{row.functional_area || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{row.department || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{row.team_1 || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{row.team_2 || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{row.team_3 || '—'}</TableCell>
                            <TableCell className="text-sm text-gray-600 whitespace-nowrap">{row.grade_level || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="import_create_fo_jobs"
                      checked={importCreateFoJobs}
                      onChange={(e) => setImportCreateFoJobs(e.target.checked)}
                      className="rounded border-gray-300 mt-0.5"
                    />
                    <label htmlFor="import_create_fo_jobs" className="text-sm text-gray-700">
                      <span className="font-medium">Create F&O jobs for new roles</span>
                      <span className="block text-xs text-gray-400">
                        Creates + links a Dynamics job for each new role (skips rows already linked). Runs in the background.
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button onClick={() => handleConfirmImport(true, false)} disabled={isImporting}>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Import New Only
                    </Button>
                    {importPreview.updates > 0 && (
                      <Button variant="outline" onClick={() => handleConfirmImport(true, true)} disabled={isImporting}>
                        Import & Update Existing
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => setImportPreview(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Import Result */}
              {importResult && (
                <div className="border rounded-lg p-4 bg-emerald-50">
                  <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" /> Import Complete
                  </h3>
                  <div className="grid grid-cols-4 gap-4 text-sm mt-3">
                    <div>Created: <strong>{importResult.created}</strong></div>
                    <div>Updated: <strong>{importResult.updated}</strong></div>
                    <div>Skipped: <strong>{importResult.skipped}</strong></div>
                    <div>Errors: <strong>{importResult.errors}</strong></div>
                  </div>
                  {importResult.error_details.length > 0 && (
                    <div className="mt-3 text-sm text-red-600">
                      {importResult.error_details.map((e, i) => <p key={i}>{e}</p>)}
                    </div>
                  )}
                  {(importResult.created > 0 || importResult.updated > 0) && (
                    <div className="mt-3 flex items-start gap-2 text-xs text-emerald-900 bg-emerald-100 border border-emerald-200 rounded px-2 py-1.5">
                      <RefreshCw className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        Sage codes on imported entries are being pushed to F&amp;O in the background.
                        <button
                          type="button"
                          className="ml-1 underline font-medium hover:no-underline"
                          onClick={() => setActiveTab('sage-sync')}
                        >
                          Open Sage Sync →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sage Code Matching */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-5 w-5" />
                Match Sage Title Codes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload a Sage staff list (with <code className="bg-gray-100 px-1 rounded">Job Title Code</code> and{' '}
                <code className="bg-gray-100 px-1 rounded">Long Description</code> columns) to match Sage codes to Role Master entries.
                These codes are required by F&amp;O when creating or updating Titles.
              </p>
              <SageCodeMatcher />
            </CardContent>
          </Card>

          {/* Derive Sage Codes */}
          <Card>
            <CardContent className="py-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                <Settings className="h-4 w-4" /> Derive Sage Codes
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Auto-generate Sage title codes for roles that don&apos;t have one. Uses abbreviation patterns from existing codes.
              </p>
              <DeriveSageCodesButton onComplete={() => loadData()} />
            </CardContent>
          </Card>

          {/* Push Sage Codes to F&O */}
          <Card>
            <CardContent className="py-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                <Settings className="h-4 w-4" /> Push Sage Codes to F&amp;O
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Writes each Role Master entry&apos;s Sage code onto the corresponding F&amp;O Title (ACCSITitleCode).
                Creates the Title in F&amp;O if it doesn&apos;t exist.
              </p>
              <PushSageCodesButton />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="budget" className="space-y-4">
          <BudgetTab />
        </TabsContent>

        <TabsContent value="profiles" className="space-y-4">
          {canSeeProfiles && activeTab === 'profiles' && <RoleProfilesTab />}
        </TabsContent>

        <TabsContent value="org-structure" className="space-y-4">
          <OrgStructureTabExtracted />
        </TabsContent>

        <TabsContent value="sage-sync" className="space-y-4">
          <SageCodeSyncPanel
            focusEntryId={(() => {
              const focus = searchParams.get('sync_focus');
              const n = focus ? parseInt(focus, 10) : NaN;
              return Number.isFinite(n) ? n : undefined;
            })()}
          />
        </TabsContent>
      </Tabs>

      {/* Detail Slide-out Panel */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedEntry && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span className="font-mono text-heritage-blue">{selectedEntry.role_code}</span>
                  <Badge className={cn('text-xs', STATUS_CONFIG[selectedEntry.status]?.color)}>
                    {STATUS_CONFIG[selectedEntry.status]?.label}
                  </Badge>
                </SheetTitle>
                <SheetDescription>{selectedEntry.title}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                {/* Core */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Job Family:</span>{' '}
                    <span className="font-medium">{selectedEntry.job_family || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Function:</span>{' '}
                    <span className="font-medium">{selectedEntry.functional_area || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Grade Level:</span>{' '}
                    <span className="font-medium">{selectedEntry.grade_level || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Position Level:</span>{' '}
                    <span className="font-medium">{selectedEntry.position_level || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Department:</span>{' '}
                    <span className="font-medium">{selectedEntry.department || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Team 1:</span>{' '}
                    <span className="font-medium">{selectedEntry.team_1 || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Team 2:</span>{' '}
                    <span className="font-medium">{selectedEntry.team_2 || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Team 3:</span>{' '}
                    <span className="font-medium">{selectedEntry.team_3 || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Academic:</span>{' '}
                    <span className="font-medium">{selectedEntry.is_academic ? 'Yes' : 'No'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Source:</span>{' '}
                    <span className="font-medium">{selectedEntry.source}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Linked Positions:</span>{' '}
                    <span className="font-medium">{selectedEntry.linked_positions_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Sage Title Code:</span>{' '}
                    <span className="font-medium">{selectedEntry.sage_title_code || '—'}</span>
                  </div>
                </div>

                {/* Remuneration */}
                {(selectedEntry.remco_grade || selectedEntry.remchannel_match_code || selectedEntry.remchannel_grade_match) && (
                  <>
                    <hr className="border-gray-200" />
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Remuneration</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Remco Grade:</span>{' '}
                        <span className="font-medium">{selectedEntry.remco_grade || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Remchannel Match Code:</span>{' '}
                        <span className="font-medium">{selectedEntry.remchannel_match_code || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Remchannel Grade Match:</span>{' '}
                        <span className="font-medium">{selectedEntry.remchannel_grade_match || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Remchannel Title Match:</span>{' '}
                        <span className="font-medium">{selectedEntry.remchannel_title_match || '—'}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Finance */}
                {(selectedEntry.finance_department_name || selectedEntry.finance_department_code) && (
                  <>
                    <hr className="border-gray-200" />
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Finance</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Finance Dept Name:</span>{' '}
                        <span className="font-medium">{selectedEntry.finance_department_name || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Finance Dept Code:</span>{' '}
                        <span className="font-medium">{selectedEntry.finance_department_code || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Role Allocation:</span>{' '}
                        <span className="font-medium">{selectedEntry.role_allocation_type || '—'}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Attributes */}
                {(selectedEntry.remote_work || selectedEntry.customer_facing !== undefined || selectedEntry.revenue_generating || selectedEntry.is_critical_role !== undefined || selectedEntry.budget_status || selectedEntry.occupational_level) && (
                  <>
                    <hr className="border-gray-200" />
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Attributes</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedEntry.remote_work && (
                        <div>
                          <span className="text-gray-500">Remote Work:</span>{' '}
                          <span className="font-medium">{selectedEntry.remote_work}</span>
                        </div>
                      )}
                      {selectedEntry.customer_facing !== undefined && selectedEntry.customer_facing !== null && (
                        <div>
                          <span className="text-gray-500">Customer Facing:</span>{' '}
                          <span className="font-medium">{selectedEntry.customer_facing ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                      {selectedEntry.revenue_generating && (
                        <div>
                          <span className="text-gray-500">Revenue Generating:</span>{' '}
                          <span className="font-medium">{selectedEntry.revenue_generating}</span>
                        </div>
                      )}
                      {selectedEntry.is_critical_role !== undefined && selectedEntry.is_critical_role !== null && (
                        <div>
                          <span className="text-gray-500">Critical Role:</span>{' '}
                          <span className="font-medium">{selectedEntry.is_critical_role ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                      {selectedEntry.is_critical_cohort !== undefined && selectedEntry.is_critical_cohort !== null && (
                        <div title="Not critical across the organisation, but a minimum number of staff is needed in this role, at each campus for instance">
                          <span className="text-gray-500">Critical Cohort:</span>{' '}
                          <span className="font-medium">{selectedEntry.is_critical_cohort ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                      {selectedEntry.budget_status && (
                        <div>
                          <span className="text-gray-500">Budget Status:</span>{' '}
                          <span className="font-medium">{selectedEntry.budget_status}</span>
                        </div>
                      )}
                      {selectedEntry.occupational_level && (
                        <div>
                          <span className="text-gray-500">Occupational Level:</span>{' '}
                          <span className="font-medium">{selectedEntry.occupational_level}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Classification - Role Clusters */}
                {(selectedEntry.role_cluster_skills || selectedEntry.role_cluster_grade_1 || selectedEntry.role_cluster_grade_2) && (
                  <>
                    <hr className="border-gray-200" />
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Classification</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedEntry.role_cluster_skills && (
                        <div>
                          <span className="text-gray-500">Role Cluster Skills:</span>{' '}
                          <span className="font-medium">{selectedEntry.role_cluster_skills}</span>
                        </div>
                      )}
                      {selectedEntry.role_cluster_grade_1 && (
                        <div>
                          <span className="text-gray-500">Grade-based 1:</span>{' '}
                          <span className="font-medium">{selectedEntry.role_cluster_grade_1}</span>
                        </div>
                      )}
                      {selectedEntry.role_cluster_grade_2 && (
                        <div>
                          <span className="text-gray-500">Grade-based 2:</span>{' '}
                          <span className="font-medium">{selectedEntry.role_cluster_grade_2}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {selectedEntry.notes && (
                  <div className="text-sm">
                    <span className="text-gray-500">Notes:</span>
                    <p className="mt-1 text-gray-700">{selectedEntry.notes}</p>
                  </div>
                )}

                {/* Dates */}
                {(selectedEntry.effective_date || selectedEntry.expiration_date) && (
                  <>
                    <hr className="border-gray-200" />
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Dates</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Effective Date:</span>{' '}
                        <span className="font-medium">{selectedEntry.effective_date ? new Date(selectedEntry.effective_date).toLocaleDateString() : '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Expiration Date:</span>{' '}
                        <span className="font-medium">{selectedEntry.expiration_date ? new Date(selectedEntry.expiration_date).toLocaleDateString() : '—'}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* F&O Linkage */}
                {(selectedEntry.fo_title_id || selectedEntry.fo_job_id) && (
                  <div className="border rounded p-3 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">F&O Linkage</h4>
                    <div className="text-sm space-y-1">
                      {selectedEntry.fo_title_id && <div>Title ID: <code>{selectedEntry.fo_title_id}</code></div>}
                      {selectedEntry.fo_job_id && <div>Job ID: <code>{selectedEntry.fo_job_id}</code></div>}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <hr className="border-gray-200" />
                <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                  <div>Created: {new Date(selectedEntry.created_at).toLocaleString()}</div>
                  {selectedEntry.updated_at && <div>Updated: {new Date(selectedEntry.updated_at).toLocaleString()}</div>}
                </div>

                {/* Role profile */}
                <RoleProfileCard roleId={selectedEntry.id} />

                {/* Change History */}
                {selectedEntry.change_logs.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Change History</h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {selectedEntry.change_logs.map((log) => (
                        <div key={log.id} className="flex items-start gap-2 text-sm border-l-2 border-gray-200 pl-3">
                          <Clock className="h-3 w-3 text-gray-400 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium">{log.change_type}</span>
                            {log.field_changed && <span className="text-gray-500"> ({log.field_changed})</span>}
                            {log.previous_value && log.new_value && (
                              <span className="text-gray-500">
                                : {log.previous_value} → {log.new_value}
                              </span>
                            )}
                            <div className="text-xs text-gray-400">
                              {log.changed_by_name || 'System'} · {new Date(log.created_at).toLocaleDateString()}
                              {log.change_reason && <span> · {log.change_reason}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <SheetFooter className="mt-6">
                <Button variant="outline" onClick={() => handleOpenEdit(selectedEntry)}>
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button variant="ghost" onClick={() => setIsDetailOpen(false)}>Close</Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create/Edit Panel */}
      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingEntry ? 'Edit Role' : 'Add New Role'}</SheetTitle>
            <SheetDescription>{editingEntry ? `Editing ${editingEntry.role_code}` : 'Create a new role master entry'}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{formError}</div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Senior Lecturer"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Function</label>
                <Select
                  value={formData.functional_area || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, functional_area: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select function" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {orgFunctions.map((f) => (
                      <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                    ))}
                    {/* Fallback: if current value not in list, show it */}
                    {formData.functional_area && !orgFunctions.find(f => f.name === formData.functional_area) && (
                      <SelectItem value={formData.functional_area}>{formData.functional_area}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Grade Level</label>
                <Select
                  value={formData.grade_level || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, grade_level: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select grade" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {filterOptions?.grade_levels.filter(Boolean).map((gl) => (
                      <SelectItem key={gl} value={gl}>{gl}</SelectItem>
                    ))}
                    {formData.grade_level && !filterOptions?.grade_levels.includes(formData.grade_level) && (
                      <SelectItem value={formData.grade_level}>{formData.grade_level}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Department</label>
              <Select
                value={formData.department || '__none__'}
                onValueChange={(v) => {
                  const dept = v === '__none__' ? undefined : v;
                  setFormData({ ...formData, department: dept, team_1: undefined, team_2: undefined, team_3: undefined });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {orgDepartments.map((d) => (
                    <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>
                  ))}
                  {formData.department && !orgDepartments.find(d => d.name === formData.department) && (
                    <SelectItem value={formData.department}>{formData.department}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {/* Team 1 */}
              <div>
                <label className="text-sm font-medium text-gray-700">Team 1</label>
                {(() => {
                  const availableT1 = orgTeams1.filter(t => t.parent_name === formData.department);
                  if (addingNewTeam?.level === 1) {
                    return (
                      <div className="flex gap-1">
                        <Input
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="New team name"
                          className="text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newTeamName.trim()) handleCreateTeam(1, newTeamName);
                            if (e.key === 'Escape') { setAddingNewTeam(null); setNewTeamName(''); }
                          }}
                        />
                        <Button size="sm" variant="outline" onClick={() => handleCreateTeam(1, newTeamName)} disabled={!newTeamName.trim()}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAddingNewTeam(null); setNewTeamName(''); }}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-1">
                      <Select
                        value={formData.team_1 || '__none__'}
                        onValueChange={(v) => {
                          if (v === '__add_new__') {
                            if (formData.department) { setAddingNewTeam({ level: 1 }); setNewTeamName(''); }
                            return;
                          }
                          setFormData({ ...formData, team_1: v === '__none__' ? undefined : v, team_2: undefined, team_3: undefined });
                        }}
                        disabled={!formData.department}
                      >
                        <SelectTrigger><SelectValue placeholder={!formData.department ? 'Select dept first' : 'Select team'} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— None —</SelectItem>
                          {availableT1.map((t) => (
                            <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                          ))}
                          {formData.team_1 && !availableT1.find(t => t.name === formData.team_1) && (
                            <SelectItem value={formData.team_1}>{formData.team_1}</SelectItem>
                          )}
                          {formData.department && <SelectItem value="__add_new__" className="text-blue-600">+ Add New</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
              </div>
              {/* Team 2 */}
              <div>
                <label className="text-sm font-medium text-gray-700">Team 2</label>
                {(() => {
                  const availableT2 = orgTeams2.filter(t => t.parent_name === formData.team_1);
                  if (addingNewTeam?.level === 2) {
                    return (
                      <div className="flex gap-1">
                        <Input
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="New team name"
                          className="text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newTeamName.trim()) handleCreateTeam(2, newTeamName);
                            if (e.key === 'Escape') { setAddingNewTeam(null); setNewTeamName(''); }
                          }}
                        />
                        <Button size="sm" variant="outline" onClick={() => handleCreateTeam(2, newTeamName)} disabled={!newTeamName.trim()}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAddingNewTeam(null); setNewTeamName(''); }}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  }
                  return (
                    <Select
                      value={formData.team_2 || '__none__'}
                      onValueChange={(v) => {
                        if (v === '__add_new__') {
                          if (formData.team_1) { setAddingNewTeam({ level: 2 }); setNewTeamName(''); }
                          return;
                        }
                        setFormData({ ...formData, team_2: v === '__none__' ? undefined : v, team_3: undefined });
                      }}
                      disabled={!formData.team_1}
                    >
                      <SelectTrigger><SelectValue placeholder={!formData.team_1 ? 'Select Team 1 first' : 'Select team'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {availableT2.map((t) => (
                          <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                        ))}
                        {formData.team_2 && !availableT2.find(t => t.name === formData.team_2) && (
                          <SelectItem value={formData.team_2}>{formData.team_2}</SelectItem>
                        )}
                        {formData.team_1 && <SelectItem value="__add_new__" className="text-blue-600">+ Add New</SelectItem>}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              {/* Team 3 */}
              <div>
                <label className="text-sm font-medium text-gray-700">Team 3</label>
                {(() => {
                  const availableT3 = orgTeams3.filter(t => t.parent_name === formData.team_2);
                  if (addingNewTeam?.level === 3) {
                    return (
                      <div className="flex gap-1">
                        <Input
                          value={newTeamName}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="New team name"
                          className="text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newTeamName.trim()) handleCreateTeam(3, newTeamName);
                            if (e.key === 'Escape') { setAddingNewTeam(null); setNewTeamName(''); }
                          }}
                        />
                        <Button size="sm" variant="outline" onClick={() => handleCreateTeam(3, newTeamName)} disabled={!newTeamName.trim()}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAddingNewTeam(null); setNewTeamName(''); }}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  }
                  return (
                    <Select
                      value={formData.team_3 || '__none__'}
                      onValueChange={(v) => {
                        if (v === '__add_new__') {
                          if (formData.team_2) { setAddingNewTeam({ level: 3 }); setNewTeamName(''); }
                          return;
                        }
                        setFormData({ ...formData, team_3: v === '__none__' ? undefined : v });
                      }}
                      disabled={!formData.team_2}
                    >
                      <SelectTrigger><SelectValue placeholder={!formData.team_2 ? 'Select Team 2 first' : 'Select team'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {availableT3.map((t) => (
                          <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                        ))}
                        {formData.team_3 && !availableT3.find(t => t.name === formData.team_3) && (
                          <SelectItem value={formData.team_3}>{formData.team_3}</SelectItem>
                        )}
                        {formData.team_2 && <SelectItem value="__add_new__" className="text-blue-600">+ Add New</SelectItem>}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Position Level</label>
                <Input
                  value={formData.position_level || ''}
                  onChange={(e) => setFormData({ ...formData, position_level: e.target.value })}
                  placeholder="e.g. Manage Others"
                />
              </div>
              <div className="flex items-end pb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_academic"
                    checked={formData.is_academic || false}
                    onChange={(e) => setFormData({ ...formData, is_academic: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="is_academic" className="text-sm text-gray-700">Academic role</label>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <Input
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>

            {/* Sage & Remuneration */}
            <hr className="border-gray-200" />
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Remuneration & Finance</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Sage Title Code</label>
                <Input
                  value={formData.sage_title_code || ''}
                  onChange={(e) => setFormData({ ...formData, sage_title_code: e.target.value })}
                  placeholder="e.g. ACAD"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remco Grade</label>
                <Input
                  value={formData.remco_grade || ''}
                  onChange={(e) => setFormData({ ...formData, remco_grade: e.target.value })}
                  placeholder="e.g. C3"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remchannel Match Code</label>
                <Input
                  value={formData.remchannel_match_code || ''}
                  onChange={(e) => setFormData({ ...formData, remchannel_match_code: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remchannel Grade Match</label>
                <Input
                  value={formData.remchannel_grade_match || ''}
                  onChange={(e) => setFormData({ ...formData, remchannel_grade_match: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remchannel Title Match</label>
                <Input
                  value={formData.remchannel_title_match || ''}
                  onChange={(e) => setFormData({ ...formData, remchannel_title_match: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Finance Dept Name</label>
                <Input
                  value={formData.finance_department_name || ''}
                  onChange={(e) => setFormData({ ...formData, finance_department_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Finance Dept Code</label>
                <Input
                  value={formData.finance_department_code || ''}
                  onChange={(e) => setFormData({ ...formData, finance_department_code: e.target.value })}
                />
              </div>
            </div>

            {/* Attributes */}
            <hr className="border-gray-200" />
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Attributes</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Role Allocation Type</label>
                <Select
                  value={formData.role_allocation_type || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, role_allocation_type: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {ROLE_ALLOCATION_TYPES.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                    {formData.role_allocation_type
                      && !ROLE_ALLOCATION_TYPES.some(o => o.value === formData.role_allocation_type) && (
                      <SelectItem value={formData.role_allocation_type}>
                        {formData.role_allocation_type} (current)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remote Work</label>
                <Select
                  value={formData.remote_work || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, remote_work: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {REMOTE_WORK_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                    {/* Whatever this row already holds, when it predates the
                        current vocabulary — without this the trigger renders
                        blank and saving silently discards the stored value. */}
                    {formData.remote_work && !REMOTE_WORK_OPTIONS.includes(formData.remote_work) && (
                      <SelectItem value={formData.remote_work}>
                        {formData.remote_work} (current)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Revenue Generating</label>
                <Select
                  value={formData.revenue_generating || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, revenue_generating: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    <SelectItem value="No - Support">No - Support</SelectItem>
                    <SelectItem value="Yes - Sales">Yes - Sales</SelectItem>
                    <SelectItem value="Yes - Core product design / delivery">Yes - Core product design / delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Budget Status</label>
                <Select
                  value={formData.budget_status || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, budget_status: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Q2">Q2</SelectItem>
                    <SelectItem value="Q3">Q3</SelectItem>
                    <SelectItem value="Q4">Q4</SelectItem>
                    <SelectItem value="2027">2027</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Occupational Level</label>
                <Input
                  value={formData.occupational_level || ''}
                  onChange={(e) => setFormData({ ...formData, occupational_level: e.target.value })}
                />
              </div>
              <div className="flex items-end gap-6 pb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inline_customer_facing"
                    checked={formData.customer_facing || false}
                    onChange={(e) => setFormData({ ...formData, customer_facing: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="inline_customer_facing" className="text-sm text-gray-700">Customer Facing</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inline_is_critical_role"
                    checked={formData.is_critical_role || false}
                    onChange={(e) => setFormData({ ...formData, is_critical_role: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="inline_is_critical_role" className="text-sm text-gray-700">Critical Role</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inline_is_critical_cohort"
                    checked={formData.is_critical_cohort || false}
                    onChange={(e) => setFormData({ ...formData, is_critical_cohort: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="inline_is_critical_cohort" className="text-sm text-gray-700"
                         title="Not critical across the organisation, but a minimum number of staff is needed in this role, at each campus for instance">
                    Critical Cohort
                  </label>
                </div>
              </div>
              <div className="flex items-end gap-6 pb-2 col-span-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inline_essential_campus_operations"
                    checked={formData.essential_campus_operations || false}
                    onChange={(e) => setFormData({ ...formData, essential_campus_operations: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="inline_essential_campus_operations" className="text-sm text-gray-700"
                         title="Required for a campus to open">
                    Essential Campus Operations
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inline_campus_manco_member"
                    checked={formData.campus_manco_member || false}
                    onChange={(e) => setFormData({ ...formData, campus_manco_member: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="inline_campus_manco_member" className="text-sm text-gray-700"
                         title="Sits on Campus Manco or Campus Academic Management">
                    Campus Manco / Academic Mgmt
                  </label>
                </div>
              </div>
            </div>

            {/* Classification */}
            <hr className="border-gray-200" />
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Classification</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Role Cluster Skills</label>
                <Input
                  value={formData.role_cluster_skills || ''}
                  onChange={(e) => setFormData({ ...formData, role_cluster_skills: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Grade-based 1</label>
                <Input
                  value={formData.role_cluster_grade_1 || ''}
                  onChange={(e) => setFormData({ ...formData, role_cluster_grade_1: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Grade-based 2</label>
                <Input
                  value={formData.role_cluster_grade_2 || ''}
                  onChange={(e) => setFormData({ ...formData, role_cluster_grade_2: e.target.value })}
                />
              </div>
            </div>

            {/* Dates */}
            <hr className="border-gray-200" />
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Dates</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Effective Date</label>
                <Input
                  type="date"
                  value={formData.effective_date || ''}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value || undefined })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Expiration Date</label>
                <Input
                  type="date"
                  value={formData.expiration_date || ''}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value || undefined })}
                />
              </div>
            </div>

            {/* Create-only: auto-create + link the F&O (Dynamics) job for this new role */}
            {!editingEntry && (
              <>
                <hr className="border-gray-200" />
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="inline_create_fo_job"
                    checked={createFoJob}
                    onChange={(e) => setCreateFoJob(e.target.checked)}
                    className="rounded border-gray-300 mt-0.5"
                  />
                  <label htmlFor="inline_create_fo_job" className="text-sm text-gray-700">
                    <span className="font-medium">Create F&O job now</span>
                    <span className="block text-xs text-gray-400">
                      Creates the Dynamics job (Job ID = this role's code) and links it. Uncheck for planning-only roles.
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
          <SheetFooter className="mt-6">
            <Button variant="ghost" onClick={() => setIsFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving || !formData.title}>
              {isSaving ? 'Saving...' : editingEntry ? 'Save Changes' : 'Create Role'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Reassign Positions Dialog */}
      <Dialog
        open={!!reassignSource}
        onOpenChange={(open) => { if (!open) { setReassignSource(null); loadData(); } }}
      >
        <DialogContent className="max-w-4xl">
          {reassignSource && (
            <ReassignPositionsDialog
              sourceEntry={reassignSource}
              onClose={() => { setReassignSource(null); loadData(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit F&O Job Dialog */}
      <EditFoJobDialog
        open={!!editFoJobId}
        onOpenChange={(open) => { if (!open) { setEditFoJobId(null); loadData(); } }}
        jobId={editFoJobId || ''}
        defaults={editFoJobDefaults}
      />

      {/* Manual Link Search Dialog */}
      {manualLinkEntry && (
        <ManualLinkSearchDialog
          open={!!manualLinkEntry}
          onOpenChange={(open) => { if (!open) setManualLinkEntry(null); }}
          entryId={manualLinkEntry.id}
          entryTitle={manualLinkEntry.title}
          onLinked={() => { setManualLinkEntry(null); loadData(); }}
        />
      )}

      {/* Create F&O Job Dialog */}
      {createJobEntry && (
        <CreateFoJobDialog
          open={!!createJobEntry}
          onOpenChange={(open) => { if (!open) setCreateJobEntry(null); }}
          entryId={createJobEntry.id}
          entryTitle={createJobEntry.title}
          roleCode={createJobEntry.role_code}
          sageTitleCode={createJobEntry.sage_title_code}
          functionalArea={createJobEntry.functional_area}
          gradeLevel={createJobEntry.grade_level}
          onCreated={() => { setCreateJobEntry(null); loadData(); }}
        />
      )}

      {/* Auto-Link Preview Dialog */}
      <AutoLinkPreviewDialog
        open={autoLinkOpen}
        onOpenChange={setAutoLinkOpen}
        onComplete={() => { setAutoLinkOpen(false); loadData(); }}
      />
    </div>
  );
};

export default RoleMasterPage;
